import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from '@/contexts/AuthContext'
import { Toaster } from '@/components/ui/sonner'
import ErrorReporter from '@/components/ErrorReporter'
import { Suspense, lazy, useEffect } from 'react'
import { isAstroCareerUnlocked } from '@/lib/featureFlags'
import { 
  PageLoaderSkeleton,
  DashboardSkeleton,
  ChartSkeleton,
  CompatibilitySkeleton,
  CalendarSkeleton,
  HomeSkeleton,
  FormSkeleton,
  SubscribeSkeleton 
} from '@/components/skeletons/PageSkeletons'

// Lazy load pages for code splitting
const HomePage = lazy(() => import('@/pages/Home'))
const LoginPage = lazy(() => import('@/pages/Login'))
const DashboardPage = lazy(() => import('@/pages/Dashboard'))
const OnboardingPage = lazy(() => import('@/pages/Onboarding'))
const ChartPage = lazy(() => import('@/pages/Chart'))
const CompatibilityPage = lazy(() => import('@/pages/Compatibility'))
const LifeTimelinePage = lazy(() => import('@/pages/LifeTimeline'))
const CalendarPage = lazy(() => import('@/pages/Calendar'))
const HaircutCalendarPage = lazy(() => import('@/pages/HaircutCalendar'))
const SubscribePage = lazy(() => import('@/pages/Subscribe'))
const PaymentSuccessPage = lazy(() => import('@/pages/PaymentSuccess'))
const PersonaNewPage = lazy(() => import('@/pages/PersonaNew'))
const SettingsPage = lazy(() => import('@/pages/Settings'))
const MySubscriptionPage = lazy(() => import('@/pages/MySubscription'))
const Transits2026Page = lazy(() => import('@/pages/Transits2026'))
const PregnancyPlanningPage = lazy(() => import('@/pages/PregnancyPlanning'))
const ResetPasswordPage = lazy(() => import('@/pages/ResetPassword'))
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPassword'))
const AstroCareerPage = lazy(() => import('@/pages/AstroCareer'))
const AstroCareerLockPage = lazy(() => import('@/pages/AstroCareerLock'))
const YandexCallbackPage = lazy(() => import('@/pages/YandexCallback'))
const VkCallbackPage = lazy(() => import('@/pages/VkCallback'))

// Legal & Info pages
const TermsPage = lazy(() => import('@/pages/Terms'))
const PrivacyPage = lazy(() => import('@/pages/Privacy'))
const FAQPage = lazy(() => import('@/pages/FAQ'))
const RefundPage = lazy(() => import('@/pages/Refund'))
const ContactPage = lazy(() => import('@/pages/Contact'))
const PeoplePage = lazy(() => import('@/pages/People'))
const CalculationsHistoryPage = lazy(() => import('@/pages/CalculationsHistory'))

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return <PageLoaderSkeleton />
  }

  if (!user) {
    return <Navigate to="/login" state={{ redirect: location.pathname }} replace />
  }

  return <>{children}</>
}

// Auth cookie management (для совместимости с API)
function AuthCookieManager() {
  const { user } = useAuth()
  
  useEffect(() => {
    const updateCookie = async () => {
      if (user) {
        try {
          const token = await user.getIdToken()
          const isSecure = window.location.protocol === 'https:'
          document.cookie = `auth_token=${token}; path=/; max-age=3600; SameSite=Lax${isSecure ? '; Secure' : ''}`
        } catch (error) {
          console.error('Failed to set auth cookie:', error)
        }
      } else {
        document.cookie = 'auth_token=; path=/; max-age=0'
      }
    }
    updateCookie()
  }, [user])
  
  return null
}

// External scripts loader
function ExternalScripts() {
  useEffect(() => {
    // Check if scripts already loaded (prevents double-load in StrictMode)
    const existingOrchids = document.getElementById('orchids-script')
    const existingRouteMessenger = document.getElementById('route-messenger-script')
    
    // Check if RouteMessenger is already declared or is mid-load (prevents StrictMode double-load)
    const routeMessengerAlreadyLoaded = typeof (window as any).RouteMessenger !== 'undefined' || existingRouteMessenger
    const routeMessengerLoading = Boolean((window as any).__routeMessengerLoading)
    
    let orchidsScript: HTMLScriptElement | null = null
    let routeMessengerScript: HTMLScriptElement | null = null

    // Orchids browser logs
    if (!existingOrchids) {
      orchidsScript = document.createElement('script')
      orchidsScript.id = 'orchids-script'
      orchidsScript.src = 'https://slelguoygbfzlpylpxfs.supabase.co/storage/v1/object/public/scripts/orchids-browser-logs.js'
      orchidsScript.setAttribute('data-orchids-project-id', '2cc26e1a-8b13-4e0e-9b88-078e22664c19')
      orchidsScript.async = true
      document.body.appendChild(orchidsScript)
    }

    // Route messenger - only load if not already loaded
    // Also check if script is in the process of loading
    if (
      !routeMessengerAlreadyLoaded &&
      !routeMessengerLoading &&
      !document.querySelector('script[src*="route-messenger.js"]')
    ) {
      routeMessengerScript = document.createElement('script')
      routeMessengerScript.id = 'route-messenger-script'
      routeMessengerScript.src = 'https://slelguoygbfzlpylpxfs.supabase.co/storage/v1/object/public/scripts/route-messenger.js'
      routeMessengerScript.setAttribute('data-target-origin', '*')
      routeMessengerScript.setAttribute('data-message-type', 'ROUTE_CHANGE')
      routeMessengerScript.setAttribute('data-include-search-params', 'true')
      routeMessengerScript.setAttribute('data-only-in-iframe', 'true')
      routeMessengerScript.setAttribute('data-debug', 'true')
      routeMessengerScript.setAttribute('data-custom-data', '{"appName": "AstroBaby", "version": "1.0.0"}')
      routeMessengerScript.async = true
      ;(window as any).__routeMessengerLoading = true
      // Prevent double declaration by checking before appending
      routeMessengerScript.onerror = () => {
        ;(window as any).__routeMessengerLoading = false
        console.warn('RouteMessenger script failed to load')
      }
      routeMessengerScript.onload = () => {
        ;(window as any).__routeMessengerLoading = false
      }
      document.body.appendChild(routeMessengerScript)
    }

    return () => {
      // Only remove if we added them
      if (orchidsScript) orchidsScript.remove()
    }
  }, [])

  return null
}

function App() {
  return (
    <AuthProvider>
      <ErrorReporter />
      <ExternalScripts />
      <AuthCookieManager />
      <Routes>
        {/* Public routes */}
        <Route path="/" element={
          <Suspense fallback={<HomeSkeleton />}>
            <HomePage />
          </Suspense>
        } />
        <Route path="/login" element={
          <Suspense fallback={<FormSkeleton />}>
            <LoginPage />
          </Suspense>
        } />
        <Route path="/auth/forgot-password" element={
          <Suspense fallback={<FormSkeleton />}>
            <ForgotPasswordPage />
          </Suspense>
        } />
        <Route path="/auth/reset-password" element={
          <Suspense fallback={<FormSkeleton />}>
            <ResetPasswordPage />
          </Suspense>
        } />
        <Route path="/auth/action" element={
          <Suspense fallback={<FormSkeleton />}>
            <ResetPasswordPage />
          </Suspense>
        } />
        <Route path="/auth/yandex/callback" element={
          <Suspense fallback={<PageLoaderSkeleton />}>
            <YandexCallbackPage />
          </Suspense>
        } />
        <Route path="/auth/vk/callback" element={
          <Suspense fallback={<PageLoaderSkeleton />}>
            <VkCallbackPage />
          </Suspense>
        } />
        <Route path="/payment-success" element={
          <Suspense fallback={<PageLoaderSkeleton />}>
            <PaymentSuccessPage />
          </Suspense>
        } />
        
        {/* Legal & Info pages */}
        <Route path="/terms" element={
          <Suspense fallback={<PageLoaderSkeleton />}>
            <TermsPage />
          </Suspense>
        } />
        <Route path="/privacy" element={
          <Suspense fallback={<PageLoaderSkeleton />}>
            <PrivacyPage />
          </Suspense>
        } />
        <Route path="/faq" element={
          <Suspense fallback={<PageLoaderSkeleton />}>
            <FAQPage />
          </Suspense>
        } />
        <Route path="/refund" element={
          <Suspense fallback={<PageLoaderSkeleton />}>
            <RefundPage />
          </Suspense>
        } />
        <Route path="/contact" element={
          <Suspense fallback={<PageLoaderSkeleton />}>
            <ContactPage />
          </Suspense>
        } />
        <Route path="/haircut-calendar" element={
          <Suspense fallback={<PageLoaderSkeleton />}>
            <HaircutCalendarPage />
          </Suspense>
        } />
        <Route path="/astro-career" element={
          <Suspense fallback={<PageLoaderSkeleton />}>
            {isAstroCareerUnlocked() ? <AstroCareerPage /> : <AstroCareerLockPage />}
          </Suspense>
        } />
        
        {/* Protected routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Suspense fallback={<DashboardSkeleton />}>
              <DashboardPage />
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/onboarding" element={
          <ProtectedRoute>
            <Suspense fallback={<FormSkeleton />}>
              <OnboardingPage />
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/chart" element={
          <ProtectedRoute>
            <Suspense fallback={<ChartSkeleton />}>
              <ChartPage />
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/compatibility" element={
          <ProtectedRoute>
            <Suspense fallback={<CompatibilitySkeleton />}>
              <CompatibilityPage />
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/life-timeline" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoaderSkeleton />}>
              <LifeTimelinePage />
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/calendar" element={
          <ProtectedRoute>
            <Suspense fallback={<CalendarSkeleton />}>
              <CalendarPage />
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/subscribe" element={
          <ProtectedRoute>
            <Suspense fallback={<SubscribeSkeleton />}>
              <SubscribePage />
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/persona/new" element={
          <ProtectedRoute>
            <Suspense fallback={<FormSkeleton />}>
              <PersonaNewPage />
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/people" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoaderSkeleton />}>
              <PeoplePage />
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoaderSkeleton />}>
              <SettingsPage />
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/my-subscription" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoaderSkeleton />}>
              <MySubscriptionPage />
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/calculations-history" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoaderSkeleton />}>
              <CalculationsHistoryPage />
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/transits-2026" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoaderSkeleton />}>
              <Transits2026Page />
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/pregnancy-planning" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoaderSkeleton />}>
              <PregnancyPlanningPage />
            </Suspense>
          </ProtectedRoute>
        } />
        
        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster />
    </AuthProvider>
  )
}

export default App
